import { useAuth } from "../../context/AuthContext";
import { useNotification } from "../../context/NotificationContext";
import { useNavigate } from "react-router-dom";
import client from "../../api/client";
import { LoginForm } from "../../components/login-form";
import { useTelegram } from "../../hooks/useTelegram";
import TelegramLoginButton from "../../components/ui/TelegramLoginButton";

export default function Login() {
  const { login } = useAuth();
  const { add } = useNotification();
  const navigate = useNavigate();
  const { user: telegramUser, initData, isTelegram } = useTelegram();

  const redirectAfterAuth = (user) => {
    if (user?.termsAccepted === false) {
      navigate("/accept-policies", { replace: true });
      return;
    }

    const role = user.role.toUpperCase();
    if (role === "ADMIN") navigate("/admin/dashboard", { replace: true });
    else if (role === "RECRUITER") navigate("/recruiter/dashboard", { replace: true });
    else if (role === "APPLIER") navigate("/applier/dashboard", { replace: true });
    else navigate("/", { replace: true });
  };

  const handleAuthSuccess = (res) => {
    login(res.data.token, res.data.user);
    add("Login successful!", "success");
    redirectAfterAuth(res.data.user);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;

    const data = {
      email: form.email.value,
      password: form.password.value,
    };

    try {
      const res = await client.post("/auth/login", data);
      handleAuthSuccess(res);
    } catch (err) {
      add(err.response?.data?.message || "Login failed", "error");
    }
  };

  const handleTelegramWebAppLogin = async () => {
    if (!telegramUser || !initData) {
      add("Telegram user data missing. Are you inside Telegram?", "error");
      return;
    }

    try {
      const res = await client.post("/auth/telegram", { initData });
      handleAuthSuccess(res);
    } catch (err) {
      add(err.response?.data?.message || "Telegram login failed", "error");
    }
  };

  const handleWidgetLogin = async (user) => {
    try {
      const res = await client.post("/auth/telegram", { widgetData: user });
      handleAuthSuccess(res);
    } catch (err) {
      add(err.response?.data?.message || "Telegram widget login failed", "error");
    }
  };

  return (
    <div className="min-h-screen ">
      <div className="grid min-h-screen lg:grid-cols-2">
        <div className="hidden lg:flex flex-col justify-center p-8 lg:p-12 ">
          <div className="max-w-lg mx-auto">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              Welcome back to{" "}
              <span className="text-primary">Shebeka</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Connect with top employers, discover opportunities, and take the next step in your career journey.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md space-y-6">
            <LoginForm onSubmit={onSubmit} className="w-full" />

            {(isTelegram || !isTelegram) && (
              <div className="space-y-4">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className=" px-2 text-muted-foreground">Or continue with</span>
                  </div>
                </div>

                {isTelegram ? (
                  <button
                    onClick={handleTelegramWebAppLogin}
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                    </svg>
                    Login with Telegram
                  </button>
                ) : (
                  <TelegramLoginButton botName="shebekabot" onAuth={handleWidgetLogin} />
                )}

                <p className="text-center text-xs text-muted-foreground">
                  New to Shebeka?{" "}
                  <a href="/register" className="text-primary hover:underline">
                    Create an account
                  </a>{" "}
                  or open via Telegram to accept policies on first sign-in.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
